import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '../ui/dialog';
import { API } from '@/App';
import { PenLine, Trash2, Loader2, FileSignature } from 'lucide-react';

// Finger / stylus / mouse signature pad (DPR-aware, tracks the pointer at any width).
const SignaturePad = ({ onChange }) => {
  const ref = useRef(null); const drawing = useRef(false); const inked = useRef(false);
  useEffect(() => {
    const c = ref.current; const ctx = c.getContext('2d');
    const setup = () => {
      const r = c.getBoundingClientRect(); const dpr = window.devicePixelRatio || 1;
      c.width = Math.max(1, Math.round(r.width * dpr)); c.height = Math.max(1, Math.round(r.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#0E2A47';
    };
    setup();
    const pos = (e) => { const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: t.clientX - r.left, y: t.clientY - r.top }; };
    const start = (e) => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); inked.current = true; e.preventDefault(); };
    const end = () => { if (drawing.current) { drawing.current = false; onChange(inked.current ? c.toDataURL('image/png') : ''); } };
    c.addEventListener('mousedown', start); c.addEventListener('mousemove', move); window.addEventListener('mouseup', end);
    c.addEventListener('touchstart', start, { passive: false }); c.addEventListener('touchmove', move, { passive: false }); c.addEventListener('touchend', end);
    window.addEventListener('resize', setup);
    return () => { c.removeEventListener('mousedown', start); c.removeEventListener('mousemove', move); window.removeEventListener('mouseup', end); c.removeEventListener('touchstart', start); c.removeEventListener('touchmove', move); c.removeEventListener('touchend', end); window.removeEventListener('resize', setup); };
  }, [onChange]);
  const clear = () => { const c = ref.current; const ctx = c.getContext('2d'); ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, c.width, c.height); ctx.restore(); inked.current = false; onChange(''); };
  return (
    <div>
      <canvas ref={ref} width={460} height={120} data-testid="nda-signature-pad"
        className="w-full touch-none rounded-lg border-2 border-dashed border-slate-300 bg-white" />
      <button type="button" onClick={clear} data-testid="nda-signature-clear"
        className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
        <Trash2 size={12} /> Clear signature
      </button>
    </div>
  );
};

export default function ProviderNdaEsignDialog({ provider, token, onClose, onSigned }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [tpl, setTpl] = useState(null);
  const [signerName, setSignerName] = useState(provider?.contact_name || '');
  const [signerTitle, setSignerTitle] = useState('');
  const [sig, setSig] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/service-providers/documents/nda/template`, h).then((r) => setTpl(r.data)).catch(() => {});
  }, []);

  async function submit() {
    if (!signerName.trim()) return toast.error('Enter the signer\u2019s full name');
    if (!sig) return toast.error('Please draw a signature');
    if (!consent) return toast.error('Please confirm the electronic-signature consent');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/service-providers/${provider.id}/documents/nda/esign`,
        { signer_name: signerName.trim(), signer_title: signerTitle.trim(), signature_png: sig, consent: true }, h);
      toast.success('Agreement e-signed \u2014 attached as On File \u00b7 Awaiting Verification');
      onSigned?.(r.data);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not e-sign the agreement');
    } finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="nda-esign-dialog"
        onInteractOutside={(e) => e.preventDefault()}>
        <div className="flex items-center gap-2">
          <FileSignature size={18} className="text-[#0E2A47]" />
          <h3 className="text-lg font-bold text-[#0E2A47]">E-Sign NDA / Non-Solicitation</h3>
        </div>
        <p className="text-xs text-slate-500">
          {tpl ? `${tpl.title} \u00b7 ${tpl.revision}` : 'Loading agreement\u2026'} — TFS standard template. The signed PDF is
          attached to this provider and a Global Administrator still verifies it before it counts toward qualification.
        </p>

        {tpl && (
          <div className="max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-600"
            data-testid="nda-agreement-text">
            <p className="mb-2">{tpl.preamble}</p>
            {tpl.clauses.map((c) => (
              <div key={c.heading} className="mb-1.5">
                <span className="font-semibold text-slate-700">{c.heading}.</span> {c.body}
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="block"><span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Signer full name *</span>
            <input value={signerName} onChange={(e) => setSignerName(e.target.value)} data-testid="nda-signer-name"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#1763B6]" />
          </label>
          <label className="block"><span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Title (optional)</span>
            <input value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} data-testid="nda-signer-title"
              placeholder="e.g. Owner, Officer" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#1763B6]" />
          </label>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="mb-1 text-xs font-bold uppercase text-slate-500">Draw signature</div>
          <SignaturePad onChange={setSig} />
          <label className="mt-2 flex items-start gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="nda-consent" className="mt-0.5" />
            <span>I am authorized to sign on behalf of the Provider, I adopt the drawn mark above as my legal
              electronic signature, and I agree to be bound by this Agreement (ESIGN / UETA).</span>
          </label>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-slate-200 px-4 py-2 text-sm" data-testid="nda-esign-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="nda-esign-submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <PenLine size={15} />} Sign &amp; attach
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
